<template>
  <div class="py-4">
    <!-- Carpeta -->
    <div class="text-h6 font-weight-bold mb-1">Fotos por carpeta</div>
    <div class="caption grey--text mb-4">
      Nombrá cada foto con el código de barras, el SKU o el código del proveedor
      (7790001234567.jpg, 7790001234567_2.jpg). Solo se suben solas las que
      coinciden con un único producto.
    </div>

    <div
      class="zona pa-8 text-center mb-4"
      :class="{ 'zona--activa': arrastrando }"
      @dragover.prevent="arrastrando = true"
      @dragleave.prevent="arrastrando = false"
      @drop.prevent="soltar"
    >
      <v-icon large color="grey">mdi-folder-image</v-icon>
      <div class="mt-2">Arrastrá la carpeta o las fotos acá</div>
      <div class="mt-3">
        <v-btn small outlined class="text-none mr-2" @click="$refs.carpeta.click()">Elegir carpeta</v-btn>
        <v-btn small outlined class="text-none" @click="$refs.archivos.click()">Elegir fotos</v-btn>
      </div>
      <input ref="carpeta" type="file" webkitdirectory multiple hidden @change="elegir" />
      <input ref="archivos" type="file" accept="image/*" multiple hidden @change="elegir" />
    </div>

    <v-progress-linear v-if="emparejando" indeterminate class="mb-4" />

    <v-card v-if="filas.length" outlined class="mb-8">
      <v-card-title class="subtitle-1 font-weight-bold">
        {{ filas.length }} fotos ·
        <span class="green--text ml-1">{{ contar("unico") }} con un producto</span>
        <span class="orange--text ml-2">{{ contar("ambiguo") }} dudosas</span>
        <span class="grey--text ml-2">{{ contar("sin_coincidencia") }} sin producto</span>
        <v-spacer />
        <v-btn
          depressed
          color="primary"
          class="text-none"
          :disabled="!aSubir.length || subiendo"
          :loading="subiendo"
          @click="subirTodas"
        >
          Subir {{ aSubir.length }} fotos
        </v-btn>
      </v-card-title>
      <v-progress-linear v-if="subiendo" :value="(subidas / totalSubida) * 100" />

      <v-simple-table dense>
        <thead>
          <tr>
            <th style="width: 40px"></th>
            <th style="width: 64px"></th>
            <th>Archivo</th>
            <th>Producto</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="f in filas" :key="f.archivo">
            <td>
              <v-checkbox
                v-model="f.subir"
                :disabled="!f.pe_id || f.estadoSubida === 'ok'"
                dense
                hide-details
                class="mt-0"
              />
            </td>
            <td><img :src="f.url" class="miniatura" /></td>
            <td class="caption">{{ f.archivo }}</td>
            <td>
              <v-select
                v-if="f.estado === 'ambiguo'"
                v-model="f.pe_id"
                :items="f.productos"
                item-text="descripcion"
                item-value="pe_id"
                placeholder="¿Cuál es?"
                dense
                hide-details
                @change="f.subir = true"
              />
              <span v-else-if="f.estado === 'unico'">
                {{ f.productos[0].descripcion }}
                <span v-if="f.productos[0].tiene_imagen" class="caption orange--text ml-1">ya tiene foto</span>
              </span>
              <span v-else class="caption grey--text">Sin producto con ese código</span>
            </td>
            <td>
              <v-icon v-if="f.estadoSubida === 'ok'" small color="green">mdi-check</v-icon>
              <v-progress-circular v-else-if="f.estadoSubida === 'subiendo'" size="16" width="2" indeterminate />
              <span v-else-if="f.estadoSubida === 'error'" class="caption red--text">No se subió</span>
              <v-chip v-else x-small :color="colorEstado(f.estado)" outlined>{{ textoEstado(f.estado) }}</v-chip>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>

    <!-- Bandeja sin imagen -->
    <div class="d-flex align-center mb-1">
      <div class="text-h6 font-weight-bold">Productos sin foto</div>
      <span class="caption grey--text ml-2">{{ sinImagen.cantidad }} en total</span>
      <v-spacer />
      <v-btn icon :loading="cargandoSinImagen" @click="fetchSinImagen"><v-icon>mdi-refresh</v-icon></v-btn>
    </div>
    <div class="caption grey--text mb-4">Arrastrá una foto sobre la fila, o hacé clic en la fila y pegala (Ctrl+V).</div>

    <v-card outlined>
      <v-list dense>
        <v-list-item
          v-for="p in sinImagen.items"
          :key="p.pe_id"
          class="fila-sin-imagen"
          :class="{ 'fila-sin-imagen--activa': filaActiva === p.pe_id }"
          tabindex="0"
          @click="filaActiva = p.pe_id"
          @dragover.prevent="filaActiva = p.pe_id"
          @drop.prevent="(e) => soltarEnFila(p, e)"
          @paste="(e) => pegarEnFila(p, e)"
        >
          <v-list-item-content>
            <v-list-item-title>{{ p.descripcion }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action>
            <v-progress-circular v-if="subiendoFila === p.pe_id" size="16" width="2" indeterminate />
            <v-btn v-else small text class="text-none" :to="{ name: 'empresa.producto.detalle', params: { id_producto: p.pe_id } }">
              Ver ficha
            </v-btn>
          </v-list-item-action>
        </v-list-item>
        <v-list-item v-if="!sinImagen.items.length && !cargandoSinImagen">
          <v-list-item-content class="caption grey--text">Todos los productos tienen foto.</v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>
  </div>
</template>

<script>
const EXTENSIONES = /\.(jpe?g|png|webp|gif)$/i;
// El servidor procesa las imágenes con un pool de 2: subir más a la vez solo
// las encola y satura la memoria.
const CONCURRENCIA = 2;

/**
 * Imágenes por carpeta (centro de carga F10): emparejar fotos por nombre de
 * archivo y subirlas de a 2; bandeja de productos sin foto con arrastrar o
 * pegar por fila.
 */
export default {
  name: "ProductosImagenesCarpeta",
  data() {
    return {
      arrastrando: false,
      emparejando: false,
      filas: [],
      subiendo: false,
      subidas: 0,
      totalSubida: 0,
      sinImagen: { cantidad: 0, items: [] },
      cargandoSinImagen: false,
      filaActiva: null,
      subiendoFila: null,
    };
  },
  computed: {
    aSubir() {
      return this.filas.filter((f) => f.subir && f.pe_id && f.estadoSubida !== "ok");
    },
  },
  methods: {
    contar(estado) {
      return this.filas.filter((f) => f.estado === estado).length;
    },
    colorEstado(estado) {
      return { unico: "green", ambiguo: "orange", sin_coincidencia: "grey" }[estado];
    },
    textoEstado(estado) {
      return { unico: "Lista", ambiguo: "Elegí el producto", sin_coincidencia: "Sin producto" }[estado];
    },
    elegir(e) {
      this.cargarArchivos(Array.from(e.target.files || []));
      e.target.value = "";
    },
    async soltar(e) {
      this.arrastrando = false;
      const archivos = await this.archivosDelDrop(e.dataTransfer);
      this.cargarArchivos(archivos);
    },
    // Una carpeta arrastrada llega como entrada de directorio: se recorre entera
    async archivosDelDrop(dataTransfer) {
      const items = Array.from(dataTransfer.items || []);
      if (!items.length || !items[0].webkitGetAsEntry) return Array.from(dataTransfer.files || []);
      const archivos = [];
      const leer = (entrada) =>
        new Promise((resolve) => {
          if (entrada.isFile) {
            entrada.file((f) => {
              archivos.push(f);
              resolve();
            }, resolve);
          } else if (entrada.isDirectory) {
            const lector = entrada.createReader();
            const leerTanda = () =>
              lector.readEntries(async (entradas) => {
                if (!entradas.length) return resolve();
                await Promise.all(entradas.map(leer));
                leerTanda();
              }, resolve);
            leerTanda();
          } else {
            resolve();
          }
        });
      await Promise.all(items.map((i) => i.webkitGetAsEntry()).filter(Boolean).map(leer));
      return archivos;
    },
    async cargarArchivos(archivos) {
      const imagenes = archivos.filter((f) => EXTENSIONES.test(f.name));
      if (!imagenes.length) return;
      this.filas.forEach((f) => URL.revokeObjectURL(f.url));
      this.emparejando = true;
      try {
        const { data } = await this.axios.post("/productos/imagenes/coincidencias", {
          archivos: imagenes.map((f) => f.name),
        });
        this.filas = data.map((r, i) => {
          const unico = r.estado === "unico" ? r.productos[0] : null;
          return {
            ...r,
            file: imagenes[i],
            url: URL.createObjectURL(imagenes[i]),
            pe_id: unico ? unico.pe_id : null,
            // Lo que ya tiene foto no se marca solo: evita duplicar
            subir: !!unico && !unico.tiene_imagen,
            estadoSubida: null,
          };
        });
      } catch (e) {
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          color: "error",
          text: (e.response && e.response.data && e.response.data.detail) || "No se pudieron revisar las fotos",
        });
      } finally {
        this.emparejando = false;
      }
    },
    async subirArchivo(peId, file) {
      const form = new FormData();
      form.append("imagenes", file);
      await this.axios.post(`/productos/productos-empresa/${peId}/imagenes/`, form);
    },
    async subirTodas() {
      const cola = [...this.aSubir];
      this.subiendo = true;
      this.subidas = 0;
      this.totalSubida = cola.length;
      const trabajador = async () => {
        while (cola.length) {
          const fila = cola.shift();
          fila.estadoSubida = "subiendo";
          try {
            await this.subirArchivo(fila.pe_id, fila.file);
            fila.estadoSubida = "ok";
            fila.subir = false;
          } catch (e) {
            fila.estadoSubida = "error";
          }
          this.subidas += 1;
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCIA }, trabajador));
      this.subiendo = false;
      const errores = this.filas.filter((f) => f.estadoSubida === "error").length;
      this.$updateBus.$emit("showSnackBar", {
        show: true,
        color: errores ? "warning" : "success",
        text: errores ? `${errores} fotos no se subieron. Volvé a intentar.` : "Fotos subidas",
      });
      this.fetchSinImagen();
    },
    async fetchSinImagen() {
      this.cargandoSinImagen = true;
      try {
        const { data } = await this.axios.get("/productos/auditoria/inconsistencias", {
          params: { tipo: "sin_imagen" },
        });
        this.sinImagen = data.sin_imagen;
      } finally {
        this.cargandoSinImagen = false;
      }
    },
    async subirEnFila(producto, file) {
      if (!file || !EXTENSIONES.test(file.name || "x.png")) return;
      this.subiendoFila = producto.pe_id;
      try {
        await this.subirArchivo(producto.pe_id, file);
        this.sinImagen.items = this.sinImagen.items.filter((p) => p.pe_id !== producto.pe_id);
        this.sinImagen.cantidad -= 1;
      } catch (e) {
        this.$updateBus.$emit("showSnackBar", { show: true, color: "error", text: "No se pudo subir la foto" });
      } finally {
        this.subiendoFila = null;
      }
    },
    soltarEnFila(producto, e) {
      this.subirEnFila(producto, (e.dataTransfer.files || [])[0]);
    },
    pegarEnFila(producto, e) {
      const item = Array.from(e.clipboardData.items || []).find((i) => i.type.startsWith("image/"));
      if (item) this.subirEnFila(producto, item.getAsFile());
    },
  },
  created() {
    this.fetchSinImagen();
  },
  beforeDestroy() {
    this.filas.forEach((f) => URL.revokeObjectURL(f.url));
  },
};
</script>

<style scoped>
.zona {
  border: 2px dashed rgba(128, 128, 128, 0.5);
  border-radius: 12px;
}
.zona--activa {
  border-color: var(--v-primary-base);
}
.miniatura {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
}
.fila-sin-imagen--activa {
  outline: 2px solid var(--v-primary-base);
}
</style>
